import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EmbeddedViewRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { BaseHttpService } from '@core/http/base-http.service';
import { PeopleHttpService } from '@core/http/people-http.service';
import { GlobalFilterStoredService } from '@core/services/global-filter-stored.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EntityName } from '@shared/enums/entity-name.enum';
import { IconType, InternalIcon } from '@shared/enums/internal-icon.enum';
import { InfiniteScrollDataAdapter } from '@shared/helpers/infinite-scroll-data.adapter';
import {
  AttributeDescription,
  LinkItem,
} from '@shared/interfaces/attribute-description.interface';
import { BaseEntityInterface } from '@shared/interfaces/base/base-entity.interface';
import { GlobalFilterCriteria } from '@shared/interfaces/filters/global-filter.interface';
import { Entity } from '@shared/models/entity.model';
import { StringReplacementPipe } from '@shared/pipes/string-replacement.pipe';
import { ImageCompressUtils } from '@shared/utils/image-compress.utils';
import { StringUtils } from '@shared/utils/string-utils';
import { EditorComponent, EditorModule } from '@tinymce/tinymce-angular';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  share,
  switchMap,
} from 'rxjs';
import { Editor as TinyMCEEditor } from 'tinymce';

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  providers: [StringReplacementPipe],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    EditorModule,
    TranslateModule,
  ],
})
export class RichTextEditorComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly translateService = inject(TranslateService);
  private readonly peopleHttpService = inject(PeopleHttpService);
  private readonly sessionService = inject(SessionService);
  private readonly globalFilterStoredService = inject(
    GlobalFilterStoredService
  );
  private readonly vcr = inject(ViewContainerRef);
  private readonly stringReplacementPipe = inject(StringReplacementPipe);

  editor: TinyMCEEditor | null = null;
  editorInstance: TinyMCEEditor | null = null;

  private readonly patternSubject = new BehaviorSubject<string>('');
  private debouncedSearch$!: Observable<BaseEntityInterface[]>;
  defaultImage = environment.wordpressJipImages + 'avatar-square.png';

  @Input() formInput: UntypedFormGroup = new UntypedFormGroup({});
  @Input() controlName: string;
  @Input() attributeDescription: AttributeDescription;

  @Input() type: string;

  @Input() showLabel = false;
  @Input() labelClass = '';

  @Input() editable = true;
  @Input() selectedIndex = 0;
  @Input() currentValue;
  @Input() validatedOnTouch = false;
  @Input() hideLink = false;
  @Input() styleClass = '';

  @Input() language: string;
  @Input() customUploadButtonTemplate: TemplateRef<void>;

  @Input() fileLoadingDisplayed = true;

  @Input() disabled = false;

  @Input() httpService: BaseHttpService<Entity>;

  searchData$: InfiniteScrollDataAdapter;

  @Input() entityName: EntityName;
  @Input() options: {
    id?: number;
    defaultLabel?: string;
    placeholder?: string;
    isViewAsText?: boolean;
    replaceAsStarKey?: string;
    shouldUseDefaultDate?: boolean;
    enabledAddNew?: boolean;
    fixedItems?: LinkItem[];
    isWideView?: boolean;
    areRowsLimited?: boolean;
    showHiddenMarkBackground?: boolean;
    elementId?: string;
    isOptional?: boolean;

    showLanguageIndicator?: boolean;
    showLanguageSwitcher?: boolean;

    readonly?: boolean;
    allowEmojiOnRTE?: boolean;
    multiLocale?: boolean;
    allowImgOnRTE?: boolean;
    imgCroppingEnabled?: boolean;
    preventDisableControl?: boolean;
    aspectRatio?: number;
    smartTyping?: boolean;
    hasTagSuggestions?: boolean;
    darkMode?: boolean;
  } = {};

  @Output() valueChanged = new EventEmitter<string>();

  loadingTpl = viewChild<TemplateRef<unknown>>('mentionLoadingTpl');
  emptyTpl = viewChild<TemplateRef<unknown>>('mentionEmptyTpl');

  private loadingViewRef: EmbeddedViewRef<unknown> | null = null;
  private emptyViewRef: EmbeddedViewRef<unknown> | null = null;

  useDarkMode = !!this.options.darkMode;
  isSmallScreen = globalThis.matchMedia('(max-width: 1023.5px)').matches;
  isFirefox = /firefox/i.test(globalThis.navigator?.userAgent ?? '');
  init: EditorComponent['init'] = {
    base_url: '/wp-content/themes/jip/assets/js/integration/tinymce/',
    suffix: '.min',
    // Bust stale browser cache of the self-hosted plugins/model/skin/icons after a
    // version bump. Without this, an upgraded core loads old cached resources and
    // media embeds (e.g. YouTube) intermittently fail until the user clears cache.
    cache_suffix: '?v=8.6.0',
    license_key: 'gpl',
    selector: 'textarea#open-source-plugins',
    plugins:
      'preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons accordion',
    editimage_cors_hosts: ['picsum.photos'],
    // menubar: 'file edit view insert format tools table help',
    menubar: false,
    toolbar:
      'undo redo | accordion accordionremove | blocks fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image | table media | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | code fullscreen preview | pagebreak anchor codesample | ltr rtl',
    font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt',
    autosave_ask_before_unload: false,
    autosave_interval: '30s',
    autosave_prefix: '{path}{query}-{id}-',
    autosave_restore_when_empty: false,
    autosave_retention: '2m',
    image_advtab: true,
    image_dimensions: false,
    max_width: 10000,
    link_list: [{ title: 'My page 1', value: 'https://www.tiny.cloud' }],
    image_list: [{ title: 'My page 1', value: 'https://www.tiny.cloud' }],
    image_class_list: [
      { title: 'None', value: '' },
      { title: 'Some class', value: 'class-name' },
    ],
    importcss_append: true,
    convert_urls: false,
    relative_urls: false,
    remove_script_host: false,
    extended_valid_elements:
      'iframe[src|width|height|frameborder|allow|allowfullscreen|referrerpolicy]',
    valid_children: '+body[style],+iframe[referrerpolicy]',
    schema: 'html5',
    verify_html: false,
    file_picker_callback: (callback, value, meta) => {
      /* Provide file and text for the link dialog */
      if (meta.filetype === 'file') {
        callback('https://www.google.com/logos/google.jpg', {
          text: 'My text',
        });
      }

      /* Provide image and alt text for the image dialog */
      if (meta.filetype === 'image') {
        callback('https://www.google.com/logos/google.jpg', {
          alt: 'My alt text',
        });
      }

      /* Provide alternative source and posted for the media dialog */
      if (meta.filetype === 'media') {
        callback('movie.mp4', {
          source2: 'alt.ogg',
          poster: 'https://www.google.com/logos/google.jpg',
        });
      }
    },
    height: 400,
    image_caption: true,
    quickbars_selection_toolbar:
      'bold italic | quicklink h2 h3 blockquote quickimage quicktable',
    noneditable_class: 'mceNonEditable',
    toolbar_mode: 'sliding',
    quickbars_insert_toolbar: 'quickimage media quicktable',
    contextmenu: 'link image table',
    skin: this.useDarkMode ? 'oxide-dark' : 'oxide',
    content_css: this.useDarkMode ? 'dark' : 'default',
    promotion: false,
    content_style:
      'body { font-family:Helvetica,Arial,sans-serif; font-size:16px } ' +
      '.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: #7D7D7D; font-size: 16px; opacity: 0.6;font-family: "trebuchet ms";}' +
      'img { max-width: 100%; height: auto; }',
    body_class: 'jip-tinymce-content',
    templates: [
      {
        title: 'Date modified example',
        description:
          'Adds a timestamp indicating the last time the document modified.',
        content:
          '<p>Last Modified: <time class="mdate">This will be replaced with the date modified.</time></p>',
      },
      {
        title: 'Replace values example',
        description:
          'These values will be replaced when the template is inserted into the editor content.',
        content: '<p>Name: {$username}, StaffID: {$staffid}</p>',
      },
      {
        title: 'Replace values preview example',
        description:
          'These values are replaced in the preview, but not when inserted into the editor content.',
        content:
          '<p>Name: {$preview_username}, StaffID: {$preview_staffid}</p>',
      },
      {
        title: 'Replace values preview and content example',
        description:
          'These values are replaced in the preview, and in the content.',
        content: '<p>Name: {$inboth_username}, StaffID: {$inboth_staffid}</p>',
      },
    ],
    placeholder: this.options.placeholder,
    automatic_uploads: true,
    images_upload_handler: blobInfo =>
      ImageCompressUtils.toCompressedDataUri(blobInfo.blob()),
    file_picker_types: 'image media',
    media_url_resolver: data =>
      new Promise<{ html: string }>(resolve => {
        try {
          const youtubeId = this.getYouTubeId(data.url);
          const spotifyId = this.getSpotifyId(data.url);
          if (youtubeId) {
            resolve({
              html: `<iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/${youtubeId}"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              referrerpolicy="strict-origin-when-cross-origin">
            </iframe>`,
            });
            return;
          }
          if (spotifyId?.type && spotifyId?.id) {
            resolve({
              html: `<iframe src="https://open.spotify.com/embed/${spotifyId.type}/${spotifyId.id}" width="100%" height="152" frameborder="0" allow="encrypted-media"></iframe>`,
            });
            return;
          }
          const vimeoMatch = /https:\/\/vimeo\.com\/(\d+)/.exec(data.url);
          if (vimeoMatch?.[1]) {
            resolve({
              html: `<iframe src="https://player.vimeo.com/video/${vimeoMatch[1]}" width="640" height="360" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`,
            });
            return;
          }
          const dailymotionMatch =
            /https:\/\/www\.dailymotion\.com\/video\/([a-zA-Z0-9]+)/.exec(
              data.url
            );
          if (dailymotionMatch?.[1]) {
            resolve({
              html: `<iframe frameborder="0" width="480" height="270" src="https://www.dailymotion.com/embed/video/${dailymotionMatch[1]}" allowfullscreen allow="autoplay"></iframe>`,
            });
            return;
          }

          resolve({
            html: `<iframe frameborder="0" width="480" height="270" src="${data.url}" allowfullscreen allow="autoplay"></iframe>`,
          });
        } catch (e) {
          console.error('media_url_resolver error:', e);
          resolve({ html: 'Can not embed this media : ' + e });
        }
      }),
    setup: editor => {
      // Wikipedia math pastes as a fallback <img> whose src is blocked by CSP.
      // In Firefox, Chrome-copied markup carries `display: inline-block` + a fixed
      // `width: ~1.279ex`, so the broken image squeezes its alt text
      // ({\displaystyle f}) into that tiny box and it drops one character per line.
      // Strip the size only from images that actually fail to load, so genuine
      // images (e.g. content copied from the jip site) keep their dimensions. The
      // paste pipeline re-serializes the node, dropping any listener attached in
      // paste_postprocess, so listen on the live editor body instead. `error`
      // doesn't bubble, hence the capture-phase listener.
      if (this.isFirefox) {
        editor.on('init', () => {
          editor.getBody().addEventListener(
            'error',
            event => {
              const target = event.target as HTMLElement;
              if (target?.tagName !== 'IMG') {
                return;
              }
              target.removeAttribute('width');
              target.removeAttribute('height');
              target.style.removeProperty('width');
              target.style.removeProperty('height');
            },
            true
          );
        });
      }

      editor.on('GetContent', function (e) {
        if (e.content.includes('youtube') || e.content.includes('youtu.be')) {
          e.content = e.content.replaceAll(
            /<iframe(?![^>]*\breferrerpolicy=)/gi,
            '<iframe referrerpolicy="strict-origin-when-cross-origin"'
          );
        }
      });

      editor.on('focus mousedown', () => {
        globalThis.dispatchEvent(new Event('tinymce-focus'));
      });

      editor.on('objectresized', () => {
        this.valueChanged.emit(editor.getContent());
      });

      editor.ui.registry.addAutocompleter('mention-autocompleter', {
        trigger: '@', // Character trigger Mention features
        minChars: 0,
        columns: 1,
        fetch: async pattern => {
          // pattern '@' : '@jo'

          this.showMentionLoading(editor, '@');

          try {
            const params = {
              keyword: pattern,
            };
            const globalFilter: GlobalFilterCriteria =
              await this.globalFilterStoredService.getFilterCriteria();

            if (globalFilter?.organizationIds?.length) {
              params['organizationIds'] = [...globalFilter.organizationIds];
            }

            const response = await firstValueFrom(
              this.peopleHttpService.filter(params)
            );

            this.hideMentionLoading();

            // TinyMCE format: array { value: string, text: string, icon?: string, meta?: object }
            return response.items
              .map(user => ({
                value: user.id.toString(),
                text: `@${user.firstName} ${user.lastName}`,
                icon: `<img src="${user?.image?.url || this.defaultImage}" class="avatar-sm b-radius"
                  title="${
                    user?.image?.title ||
                    this.stringReplacementPipe.transform(
                      this.translateService.instant(
                        this.sessionService.currentUser?.id === user.id
                          ? 'Profile.Default.MyProfileImage'
                          : 'Profile.Default.Image'
                      ),
                      {
                        '{fullName}': user.firstName + ' ' + user.lastName,
                      }
                    )
                  }"
                  alt="${user?.image?.alt || 'User avatar'}"
                />`,
                meta: user,
              }))
              .slice(0, 10);
          } catch (error) {
            console.error('Error fetch users:', error);
            this.hideMentionLoading();
            return [];
          }
        },
        onAction: (autocompleteApi, rng, value, meta) => {
          // Set selection range for selected item, then replace new html content
          editor.selection.setRng(rng);

          let currentNode = editor.selection.getNode();
          while (currentNode?.classList?.contains('mention')) {
            const newRng = editor.dom.createRng();

            newRng.setStartAfter(currentNode);
            newRng.setEndAfter(currentNode);
            editor.selection.setRng(newRng);
            currentNode = editor.selection.getNode();
          }

          const user = meta;
          const peopleUrl = environment.jipUrl.people;
          const profileUrl = this.sessionService.appendLanguagePath(
            `${peopleUrl}/${value}`
          );
          const mentionHtml = `<a href="${profileUrl}" rel="noopener noreferrer" target="_blank" class="mention" data-user-id="${value}" data-user-email="${user.email || ''}">@${user.firstName} ${user.lastName}</a>&nbsp;`;

          editor.insertContent(mentionHtml);
          autocompleteApi.hide();
        },
      });

      editor.ui.registry.addAutocompleter('hashtag-autocompleter', {
        trigger: '#',
        minChars: 0,
        columns: 1,
        fetch: async pattern => {
          this.showMentionLoading(editor, '#');

          this.patternSubject.next(pattern);

          try {
            const results = await firstValueFrom(this.debouncedSearch$);

            this.hideMentionLoading();

            if (!results || results.length === 0) return [];

            return results
              .map(entity => {
                let entityName = '';
                let icon = StringUtils.getIconByType(
                  InternalIcon.Forward,
                  IconType.SVG,
                  'gray'
                );

                switch (entity.type) {
                  case 'organizations':
                    entityName = EntityName.Organization;
                    icon = StringUtils.getIconByType(
                      InternalIcon.Organization,
                      IconType.SVG,
                      'gray'
                    );
                    break;

                  case 'ventures':
                    entityName = EntityName.Venture;
                    icon = StringUtils.getIconByType(
                      InternalIcon.Unicorn,
                      IconType.SVG,
                      'gray'
                    );
                    break;

                  case 'events':
                    entityName = EntityName.Event;
                    icon = StringUtils.getIconByType(
                      InternalIcon.Calendar,
                      IconType.SVG,
                      'gray'
                    );
                    break;

                  case 'challenges':
                    entityName = EntityName.Challenge;
                    icon = StringUtils.getIconByType(
                      InternalIcon.Lightbulb,
                      IconType.SVG,
                      'gray'
                    );
                    break;
                }

                return {
                  value: entity.id.toString(),
                  text: ``,
                  icon: `<span class="hashtag-item"><img src="${icon}" class="entity-icon" /><span class="entity-name mr-1">${entityName}</span> - ${entity.displayName} </span>`,
                  meta: entity,
                };
              })
              .slice(0, 10);
          } catch (error) {
            console.error('Error fetch users:', error);
            this.hideMentionLoading();
            return [];
          }
        },

        onAction: (autocompleteApi, rng, value, meta) => {
          // Set selection range for selected item, then replace new html content
          editor.selection.setRng(rng);

          let currentNode = editor.selection.getNode();
          while (currentNode?.classList?.contains('mention')) {
            const newRng = editor.dom.createRng();

            newRng.setStartAfter(currentNode);
            newRng.setEndAfter(currentNode);
            editor.selection.setRng(newRng);
            currentNode = editor.selection.getNode();
          }

          const entity = meta;
          const entityUrl = this.sessionService.appendLanguagePath(
            `/${entity.type}/${value}`
          );
          const mentionHtml = `<a href="${entityUrl}" rel="noopener noreferrer" target="_blank" class="mention entity-hashtag" data-entity-id="${value}">${entity.displayName}</a>&nbsp;`;

          editor.insertContent(mentionHtml);
          autocompleteApi.hide();
        },
      });
    },
  };

  ngOnInit() {
    this.init.placeholder =
      this.options?.placeholder ||
      this.translateService.instant('Common.DefaultPlaceholder');
  }

  onEditorInit(event: EditorComponent): void {
    this.editorInstance = event.editor;
  }

  ngAfterViewInit() {
    this.debouncedSearch$ = this.patternSubject.pipe(
      debounceTime(500),
      switchMap(pattern => this.searchEntitiesSequentially(pattern)),
      share()
    );
  }

  ngOnDestroy() {
    this.patternSubject.complete();
    this.hideMentionLoading();
    this.hideMentionEmptyContent();
  }

  private async searchEntitiesSequentially(
    pattern = ''
  ): Promise<BaseEntityInterface[]> {
    const entitySizeLimit = 3;
    const params = {
      keyword: pattern,
      PageSize: entitySizeLimit,
    };
    const entities = ['organizations', 'ventures', 'events', 'challenges'];
    const displayFieldMap: Record<string, string> = {
      organizations: 'orgName',
      ventures: 'title',
      events: 'title',
      challenges: 'orgName',
    };

    const globalFilter: GlobalFilterCriteria =
      await this.globalFilterStoredService.getFilterCriteria();

    if (globalFilter?.organizationIds?.length) {
      params['organizationIds'] = [...globalFilter.organizationIds];
    }

    const apiCalls = entities.map(entity =>
      this.peopleHttpService.mention(`${entity}`, params).pipe(
        map(response => {
          if (!response.items) {
            return [] as BaseEntityInterface[];
          }

          return response.items.slice(0, entitySizeLimit).map(item => {
            const displayField = displayFieldMap[entity] || 'id';
            const displayName =
              (item as any)[displayField] || item.id.toString() || '';

            return {
              ...item,
              type: entity,
              displayName: displayName,
            } as BaseEntityInterface;
          });
        }),
        catchError(() => of([] as BaseEntityInterface[]))
      )
    );

    return firstValueFrom(
      forkJoin(apiCalls).pipe(
        map(resultsArray => resultsArray.flat()),
        map(flatResults => flatResults.slice(0, 20))
      )
    );
  }

  filePickerCallback(
    cb: (
      url: string,
      meta: { title?: string; alt?: string; text?: string }
    ) => void
  ) {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');

    input.addEventListener('change', e => {
      const fileInput = e.target as HTMLInputElement;
      if (fileInput.files) {
        const file = fileInput.files[0];

        const reader = new FileReader();
        reader.addEventListener('load', () => {
          if (typeof reader.result === 'string') {
            const id = 'blobid' + Date.now();
            const blobCache = this.editorInstance.editorUpload.blobCache;
            const base64 = reader.result.split(',')[1];
            const blobInfo = blobCache.create(id, file, base64);
            blobCache.add(blobInfo);

            cb(blobInfo.blobUri(), { title: file.name });
          }
        });
        reader.readAsDataURL(file);
      }
    });

    input.click();
  }

  getYouTubeId(url: string): string | null {
    // watch?v=, youtu.be, embed, shorts
    const patterns = [
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtu\.be\/([^?&]+)/,
      /youtube\.com\/embed\/([^?&]+)/,
      /youtube\.com\/shorts\/([^?&]+)/,
    ];
    for (const pattern of patterns) {
      const match = pattern.exec(url);
      if (match?.[1]) return match[1];
    }

    return null;
  }

  getSpotifyId(url: string): { type: 'episode' | 'show'; id: string } | null {
    const episodeMatch = /open\.spotify\.com\/episode\/([a-zA-Z0-9]+)/.exec(
      url
    );
    if (episodeMatch) return { type: 'episode', id: episodeMatch[1] };
    const showMatch = /open\.spotify\.com\/show\/([a-zA-Z0-9]+)/.exec(url);
    if (showMatch) return { type: 'show', id: showMatch[1] };
    return null;
  }

  get formControl(): AbstractControl {
    if (this.formInput && this.controlName) {
      return this.formInput.get(this.controlName);
    }

    return null;
  }

  get isInvalid(): boolean {
    return (
      this.formControl &&
      this.formControl.invalid &&
      (!this.validatedOnTouch ||
        this.formControl.dirty ||
        this.formControl.touched) &&
      this.hasError()
    );
  }

  get characterCount() {
    if (!this.editorInstance) return 0;

    const content = this.editorInstance.getContent() ?? '';
    const textOnly = this.removeHtmlTags(content);
    const textDecoded = this.decodeHtmlEntities(textOnly);

    return textDecoded.length + 1;
  }

  hasError(): boolean {
    if (!this.formControl) {
      return false;
    }
    return (
      this.formControl.hasError('required') ||
      this.formControl.hasError('pattern') ||
      this.formControl.hasError('integerOnly') ||
      this.formControl.hasError('maxlength') ||
      this.formControl.hasError('notEmptyOrWhitespace')
    );
  }

  private decodeHtmlEntities(input: string): string {
    const txt = document.createElement('textarea');

    txt.innerHTML = input;

    return txt.value;
  }

  private removeHtmlTags(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    return doc.body.textContent || '';
  }

  getTriggerRect(editor: TinyMCEEditor, triggerChar: string): DOMRect | null {
    const rng = editor.selection.getRng();
    if (!rng?.startContainer) return null;

    const textNode = rng.startContainer;
    if (textNode.nodeType !== Node.TEXT_NODE) return null;

    const offset = rng.startOffset;
    const textBeforeCursor = textNode.textContent?.substring(0, offset) ?? '';

    const triggerIndex = textBeforeCursor.lastIndexOf(triggerChar);
    if (triggerIndex === -1) return null;

    try {
      const r = rng.cloneRange();
      r.setStart(textNode, triggerIndex);
      r.setEnd(textNode, triggerIndex + 1);

      return r.getBoundingClientRect();
    } catch {
      return null;
    }
  }

  getIframeRect(editor: TinyMCEEditor) {
    const iframe =
      editor.iframeElement ||
      editor.getContentAreaContainer()?.querySelector('iframe');

    return iframe?.getBoundingClientRect() ?? { top: 0, left: 0 };
  }

  destroyView(viewRef: EmbeddedViewRef<unknown> | null) {
    if (!viewRef) return;
    viewRef.destroy();
  }

  attachTemplateAtPosition(
    tpl: TemplateRef<unknown>,
    top: number,
    left: number
  ): EmbeddedViewRef<unknown> {
    const viewRef = this.vcr.createEmbeddedView(tpl);

    viewRef.detectChanges();

    const el = viewRef.rootNodes[0] as HTMLElement;
    el.style.position = 'absolute';
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;

    document.body.appendChild(el);

    return viewRef;
  }

  showMentionLoading(editor: TinyMCEEditor, triggerChar: string): void {
    this.hideMentionLoading();

    const listContainer = document.querySelector(
      '.tox .tox-autocompleter .tox-menu'
    );

    if (listContainer) return;

    const rect = this.getTriggerRect(editor, triggerChar);
    if (!rect) return;

    const iframeRect = this.getIframeRect(editor);

    this.loadingViewRef = this.attachTemplateAtPosition(
      this.loadingTpl(),
      iframeRect.top + rect.bottom + window.scrollY,
      iframeRect.left + rect.left + window.scrollX
    );
  }

  hideMentionLoading() {
    this.destroyView(this.loadingViewRef);
    this.loadingViewRef = null;
  }

  showMentionEmptyContent(editor: TinyMCEEditor, triggerChar: string) {
    this.hideMentionEmptyContent();

    const rect = this.getTriggerRect(editor, triggerChar);

    if (!rect) return;

    const iframeRect = this.getIframeRect(editor);

    this.emptyViewRef = this.attachTemplateAtPosition(
      this.emptyTpl(),
      iframeRect.top + rect.bottom + window.scrollY,
      iframeRect.left + rect.left + window.scrollX
    );
  }

  hideMentionEmptyContent() {
    this.destroyView(this.emptyViewRef);
    this.emptyViewRef = null;
  }
}
